import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  XMarkIcon, 
  LinkIcon, 
  CloudArrowUpIcon,
  DocumentTextIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { contentAPI } from '../utils/api';
import toast from 'react-hot-toast';

const AddContentModal = ({ isOpen, onClose, boardId, boardName, onContentAdded }) => {
  const [activeTab, setActiveTab] = useState('url');
  const [loading, setLoading] = useState(false);
  const [urlData, setUrlData] = useState({
    url: '',
    title: '',
    description: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const handleUrlChange = (e) => {
    setUrlData({
      ...urlData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const detectUrlType = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('instagram.com')) return 'instagram';
    if (url.includes('tiktok.com')) return 'tiktok';
    if (url.includes('linkedin.com')) return 'linkedin';
    return 'website';
  };

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const contentData = {
        boardId,
        title: urlData.title || 'Untitled',
        contentType: 'website',
        contentUrl: urlData.url,
        contentText: urlData.description
      };

      // Make real API call to add content
      const response = await contentAPI.addContent(contentData);
      
      toast.success('URL added successfully!');
      onContentAdded(response.data.item);
      
      // Reset form
      setUrlData({ url: '', title: '', description: '' });
      onClose();
    } catch (error) {
      console.error('Error adding URL:', error);
      toast.error(error.response?.data?.error || 'Failed to add URL');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('boardId', boardId);
      formData.append('title', selectedFile.name);

      // Make real API call to upload file
      const response = await contentAPI.uploadFile(formData);
      
      toast.success('File uploaded successfully!');
      onContentAdded(response.data.item);
      
      // Reset form
      setSelectedFile(null);
      onClose();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.error || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('video')) return <PlayIcon className="h-8 w-8 text-red-500" />;
    if (fileType?.includes('audio')) return <PlayIcon className="h-8 w-8 text-green-500" />;
    return <DocumentTextIcon className="h-8 w-8 text-blue-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-0 border max-w-2xl shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Add Content to "{boardName}"
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('url')}
            className={`flex-1 px-6 py-4 text-sm font-medium ${
              activeTab === 'url'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <LinkIcon className="h-5 w-5 inline mr-2" />
            Add URL
          </button>
          <button
            onClick={() => setActiveTab('file')}
            className={`flex-1 px-6 py-4 text-sm font-medium ${
              activeTab === 'file'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <CloudArrowUpIcon className="h-5 w-5 inline mr-2" />
            Upload File
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'url' ? (
            <motion.form
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleUrlSubmit}
              className="space-y-4"
            >
              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
                  URL *
                </label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  required
                  value={urlData.url}
                  onChange={handleUrlChange}
                  className="input-field"
                  placeholder="https://youtube.com/watch?v=... or https://website.com/article"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Supported: YouTube, TikTok, Instagram, LinkedIn, websites
                </p>
              </div>
              
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Title (optional)
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={urlData.title}
                  onChange={handleUrlChange}
                  className="input-field"
                  placeholder="Auto-detected or enter custom title"
                />
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optional)
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={urlData.description}
                  onChange={handleUrlChange}
                  className="input-field"
                  placeholder="Add notes about this content..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !urlData.url.trim()}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Adding...' : 'Add URL'}
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.form
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleFileSubmit}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select File *
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-500 transition-colors">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.doc,.docx,.txt,.mp3,.wav,.m4a,.mp4,.mov"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-primary-600">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, DOC, TXT, MP3, WAV, MP4 up to 100MB
                    </p>
                  </label>
                </div>

                {selectedFile && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center space-x-3">
                    {getFileIcon(selectedFile.type)}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(selectedFile.size)} • {selectedFile.type || 'Unknown type'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedFile}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Uploading...' : 'Upload File'}
                </button>
              </div>
            </motion.form>
          )}
        </div>

        {/* Production Notice */}
        <div className="bg-green-50 border-t border-green-200 p-4">
          <p className="text-sm text-green-700">
            <strong>Production Ready:</strong> Content uploads are now stored in Cloudflare R2 and processed by AI. Your files and URLs are securely managed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddContentModal; 